import { useId, useState } from "react";
import { indeksWypowiedzi } from "@shared/numeracja.js";
import type { FindingKind } from "@shared/ocenaTypy.js";
import type { PowtarzaneSlowo, ZakresTrafienia } from "@shared/powtarzaneSlowa.js";
import { domyslnyMowca, czyMoznaOcenic, czyMoznaPobracRaport } from "../../lib/raportOceny.js";
import type { MowcaRaportu, RaportOceny } from "../../lib/raportOceny.js";
import type { PowtarzaneSlowaWgMowcy } from "../../lib/powtarzaneSlowa.js";
import { odmien } from "../../lib/polski.js";
import { ListaParametrow } from "../ocena/ListaParametrow.js";
import { SekcjaPowtarzanychSlow } from "../ocena/PowtarzaneSlowa.js";
import { PrzyciskRaportu } from "../ocena/PrzyciskRaportu.js";
import { GrupaWskazan, tonRodzaju } from "../ocena/Wskazania.js";
import { IconDoPoprawy, IconInformacja, IconLink, IconOdswiez, IconSukces } from "../icons/index.js";
import { Badge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { Card } from "../ui/Card.js";
import { Field } from "../ui/Field.js";
import { SegmentedControl } from "../ui/SegmentedControl.js";
import { Select } from "../ui/Select.js";
import { StanLadowania, StanPusty } from "../ui/stany.js";
import { Tooltip } from "../ui/Tooltip.js";

/**
 * Przewija transkrypt do wypowiedzi o tym indeksie (od 0). `trafienia` przychodza tylko z listy "Powtarzane slowa":
 * miejsca slowa w tej wypowiedzi, ktore transkrypt zaznacza na chwile. Przyciski przy wskazaniach podaja sam indeks.
 */
export type PokazWypowiedz = (indeks: number, trafienia?: readonly ZakresTrafienia[]) => void;

/**
 * Karta "Ocena wg metodyki": raport z oceny parametrami metodyki w podziale na mowcow.
 *
 * Karta nigdy nie myli braku wskazan z brakiem oceny. Mowia o tym stany (patrz lib/raportOceny.ts):
 * nie oceniono, ocena czeka na zatwierdzenie podzialu albo na wczesniejsze etapy, ocena w toku, oceniono bez
 * wskazan (uczciwy wynik) oraz ocena ucieta, niezgodna albo niepoliczona z powodem. Lista parametrow ze stanem
 * kazdego z nich stoi nad wskazaniami, wiec nawet raport z samymi zerami mowi, co faktycznie sie policzylo.
 *
 * Wskazania ida sekcjami "Co wyszlo najlepiej" i "Do poprawy", w kazdej pogrupowane po parametrze (z tytulem po
 * polsku), dla JEDNEGO wybranego mowcy (lista z session.speakers). Pod nimi stoja stale cwiczenia z metodyki dla
 * parametrow, w ktorych ten mowca ma cos do poprawy.
 *
 * Cytat NIEZWERYFIKOWANY zostaje tutaj z jawna plakietka i NIE jest w transkrypcie ani zaznaczony (zloty marker,
 * szare podkreslenie), ani oznaczony litera, ani nie barwi dymka. Przy dymku stoi tylko wskazanie z ta sama
 * plakietka i cytatem, ktorego nie znaleziono. To zabezpieczenie przed halucynacja modelu: zaznaczenie fragmentu,
 * ktorego w wypowiedzi nie ma, byloby twierdzeniem, ze klient powiedzial cos, czego nie powiedzial.
 *
 * Pliki Word i PDF z tej samej oceny obejmuja wszystkich mowcow, niezaleznie od wyboru na ekranie - mowi o tym zdanie
 * nad lista parametrow.
 *
 * Na koncu wskazan wybranego mowcy stoi sekcja "Powtarzane slowa" (components/ocena/PowtarzaneSlowa.tsx): liczenie slow
 * z listy metodyki, nie ocena, wiec w neutralnych kolorach. Tylko w widoku administratora i tylko wtedy, gdy u tego mowcy
 * cos sie powtarza.
 */

function podsumowanieMowcy(mowca: MowcaRaportu): string {
  const mocne = mowca.liczbaMocnychStron;
  const poprawy = mowca.liczbaDoPoprawy;
  return (
    `${mocne} ${odmien(mocne, "mocna strona", "mocne strony", "mocnych stron")}, ` +
    `${poprawy} ${odmien(poprawy, "rzecz do poprawy", "rzeczy do poprawy", "rzeczy do poprawy")}`
  );
}

/** SG-3: do pieciu opcji przelacznik segmentowy, wiecej - lista rozwijana. */
const MAKS_SEGMENTOW = 5;

function WyborMowcy({
  mowcy,
  klucz,
  onZmiana,
}: {
  mowcy: readonly MowcaRaportu[];
  klucz: string;
  onZmiana: (klucz: string) => void;
}): React.JSX.Element {
  const id = useId();
  const opcje = mowcy.map((m) => ({ wartosc: m.klucz, etykieta: m.nazwa }));

  if (mowcy.length > MAKS_SEGMENTOW) {
    return (
      <Field etykieta="Wskazania dla mówcy" id={`${id}-mowca`} waskie>
        {(atrybuty) => <Select {...atrybuty} wartosc={klucz} opcje={opcje} onZmiana={onZmiana} />}
      </Field>
    );
  }

  return (
    <div className="ocena-wybor-mowcy">
      <span className="field-label">Wskazania dla mówcy</span>
      <SegmentedControl etykieta="Mówca" opcje={opcje} wartosc={klucz} onZmiana={onZmiana} />
    </div>
  );
}

/**
 * Naglowek sekcji wskazan: znak sekcji (ikona w tincie: zloto przy mocnych stronach, szarosc przy rzeczach do poprawy),
 * tytul i licznik. Tytul zostaje samym tekstem w <h3>, a znak i licznik sa obok niego i dla czytnika ekranu ich nie ma
 * (aria-hidden): liczby mowi juz zdanie nad sekcjami ("10 mocnych stron, 3 rzeczy do poprawy"), wiec nie czyta sie ich
 * drugi raz. Kolor sam nic nie znaczy (ST-3): ikona i tytul to tekst i ksztalt.
 */
function NaglowekSekcji({
  rodzaj,
  id,
  liczba,
  children,
}: {
  rodzaj: FindingKind;
  id: string;
  liczba: number;
  children: string;
}): React.JSX.Element {
  return (
    <div className="ocena-sekcja__naglowek">
      <span className="ocena-sekcja__znak" aria-hidden="true">
        {rodzaj === "mocna-strona" ? <IconSukces size={16} /> : <IconDoPoprawy size={16} />}
      </span>
      <h3 className="ocena-sekcja__tytul" id={id}>
        {children}
      </h3>
      {/* Zero wskazan nie niesie oceny: "nic do poprawy" nie jest szare, a brak mocnych stron nie jest zloty. */}
      <span aria-hidden="true">
        <Badge wariant={liczba === 0 ? "neutralny" : tonRodzaju(rodzaj)}>{liczba}</Badge>
      </span>
    </div>
  );
}

function SekcjaMowcy({
  mowca,
  onPokazWypowiedz,
  powtarzaneSlowa,
}: {
  mowca: MowcaRaportu;
  onPokazWypowiedz: PokazWypowiedz;
  /** Powtarzane slowa tego mowcy. Brak albo pusta lista = bez sekcji. */
  powtarzaneSlowa?: readonly PowtarzaneSlowo[];
}): React.JSX.Element {
  const brakWskazan = mowca.liczbaMocnychStron + mowca.liczbaDoPoprawy === 0;
  // Sekcja mowi numerami wypowiedzi (od 1), a transkrypt przewija sie po indeksie z bazy (od 0). Pusta lista nic nie rysuje.
  const powtorzenia = powtarzaneSlowa ? (
    <SekcjaPowtarzanychSlow
      slowa={powtarzaneSlowa}
      onPokaz={(numer, trafienia) => onPokazWypowiedz(indeksWypowiedzi(numer), trafienia)}
      idNaglowka={`${mowca.klucz}-powtarzane`}
    />
  ) : null;

  return (
    <div className="ocena-mowca" role="region" aria-label={`Wskazania: ${mowca.nazwa}`}>
      <p className="ocena-mowca__nazwa">
        <strong>{mowca.nazwa}</strong>
        <span className="ocena-mowca__liczby">{podsumowanieMowcy(mowca)}</span>
      </p>

      {brakWskazan ? (
        <>
          <StanPusty
            tytul={`Brak wskazań dla: ${mowca.nazwa}`}
            opis="Oceniono wybrane parametry i nic w wypowiedziach tego mówcy nie wymagało wskazania. To wynik oceny, a nie jej brak."
          />
          {powtorzenia}
        </>
      ) : (
        <>
          <section
            className="ocena-sekcja"
            data-rodzaj="mocna-strona"
            data-pusta={mowca.liczbaMocnychStron === 0 ? "true" : undefined}
            aria-labelledby={`${mowca.klucz}-najlepiej`}
          >
            <NaglowekSekcji rodzaj="mocna-strona" id={`${mowca.klucz}-najlepiej`} liczba={mowca.liczbaMocnychStron}>
              Co wyszło najlepiej
            </NaglowekSekcji>
            {mowca.mocneStrony.length > 0 ? (
              mowca.mocneStrony.map((g) => (
                <GrupaWskazan grupa={g} rodzaj="mocna-strona" onPokaz={onPokazWypowiedz} key={g.slug} />
              ))
            ) : (
              <p className="ocena-sekcja__pusto">Żaden z ocenionych parametrów nie wskazał mocnej strony tego mówcy.</p>
            )}
          </section>

          <section
            className="ocena-sekcja"
            data-rodzaj="do-poprawy"
            data-pusta={mowca.liczbaDoPoprawy === 0 ? "true" : undefined}
            aria-labelledby={`${mowca.klucz}-do-poprawy`}
          >
            <NaglowekSekcji rodzaj="do-poprawy" id={`${mowca.klucz}-do-poprawy`} liczba={mowca.liczbaDoPoprawy}>
              Do poprawy
            </NaglowekSekcji>
            {mowca.doPoprawy.length > 0 ? (
              mowca.doPoprawy.map((g) => (
                <GrupaWskazan grupa={g} rodzaj="do-poprawy" onPokaz={onPokazWypowiedz} key={g.slug} />
              ))
            ) : (
              <p className="ocena-sekcja__pusto">Żaden z ocenionych parametrów nie wskazał rzeczy do poprawy u tego mówcy.</p>
            )}
          </section>

          {mowca.cwiczenia.length > 0 && (
            <section className="ocena-sekcja" aria-labelledby={`${mowca.klucz}-cwiczenia`}>
              <h3 className="ocena-sekcja__tytul" id={`${mowca.klucz}-cwiczenia`}>
                Ćwiczenia
              </h3>
              <p className="field-hint ocena-sekcja__lead">
                Stały tekst z metodyki, bez udziału modelu. Dotyczy parametrów, w których ten mówca ma rzeczy do poprawy.
              </p>
              <ul className="ocena-lista ocena-cwiczenia">
                {mowca.cwiczenia.map((c) => (
                  <li className="ocena-cwiczenie" key={c.slug}>
                    <h4 className="ocena-grupa__tytul">{c.tytul}</h4>
                    <p className="ocena-cwiczenie__tekst">{c.tekst}</p>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {powtorzenia}
        </>
      )}
    </div>
  );
}

function TrescRaportu({
  raport,
  onPokazWypowiedz,
  uproszczony,
  powtarzaneSlowa,
}: {
  raport: RaportOceny;
  onPokazWypowiedz: PokazWypowiedz;
  /** Widok konta `user`: bez listy parametrow ze stanami technicznymi, bez modelu i bez odwolan do "Ocen ponownie". */
  uproszczony: boolean;
  powtarzaneSlowa?: PowtarzaneSlowaWgMowcy;
}): React.JSX.Element {
  const [wybranyKlucz, setWybranyKlucz] = useState<string | null>(null);

  // Wybrany mowca przezywa odswiezenia sesji (id mowcy sie nie zmienia przy zmianie nazwy). Gdy go juz nie ma - domyslny.
  const klucz = raport.mowcy.some((m) => m.klucz === wybranyKlucz) ? wybranyKlucz : domyslnyMowca(raport.mowcy);
  const mowca = raport.mowcy.find((m) => m.klucz === klucz) ?? null;

  // Raport o czyms mowi dopiero wtedy, gdy choc jeden parametr sie policzyl. Inaczej same plakietki z powodami.
  const cosOceniono = raport.parametry.some((p) => p.stan.rodzaj === "oceniono" || p.stan.rodzaj === "brak-wskazan");
  const { uwagaZadania } = raport;

  return (
    <>
      <p className="field-hint ocena-word">
        Pliki Word i PDF zawierają wszystkich mówców i wszystkie wybrane parametry, niezależnie od mówcy wybranego na ekranie.
      </p>

      {!uproszczony && (
        <>
          <h3 className="ocena-sekcja__tytul ocena-sekcja__tytul--parametry">Parametry oceny</h3>
          <ListaParametrow parametry={raport.parametry} faza={raport.faza} />
        </>
      )}

      {uwagaZadania && (
        <p className="ocena-uwaga">
          {/* Ikona informacji w kolorze informacji (session.css, .ocena-uwaga svg), jak na banerze informacji: trojkat
              ostrzezenia w niebieskim mowilby dwie rozne rzeczy naraz, a bursztyn to w tej karcie zloto oceny. */}
          <IconInformacja size={14} />
          {uproszczony ? (
            // Bez przycisku "Ocen ponownie" (user go nie ma) i bez technicznego szczegolu bledu.
            <span>
              {uwagaZadania.rodzaj === "porazka"
                ? "Ocena nie została dokończona."
                : "Nie wszystkie wyniki oceny udało się zapisać."}
            </span>
          ) : (
            <span>
              {uwagaZadania.rodzaj === "porazka"
                ? "Ocena nie została dokończona. Spróbuj jeszcze raz przyciskiem „Oceń ponownie”."
                : "Nie wszystkie wyniki udało się zapisać. Parametry oznaczone jako „Nie policzona” zleć jeszcze raz przyciskiem „Oceń ponownie”."}
              <span className="ocena-uwaga__szczegol">{` Szczegół: ${uwagaZadania.tekst}`}</span>
            </span>
          )}
        </p>
      )}

      {cosOceniono && mowca && (
        <>
          {raport.mowcy.length > 1 && (
            <WyborMowcy mowcy={raport.mowcy} klucz={mowca.klucz} onZmiana={setWybranyKlucz} />
          )}
          <SekcjaMowcy
            mowca={mowca}
            onPokazWypowiedz={onPokazWypowiedz}
            // Konto `user` tej sekcji nie dostaje (nie zasypujemy klienta tekstem), nawet gdyby wolajacy podal liste.
            powtarzaneSlowa={uproszczony ? undefined : powtarzaneSlowa?.get(mowca.klucz)}
            key={mowca.klucz}
          />
        </>
      )}

      {!uproszczony && (raport.wersjaMetodyki || raport.modele.length > 0) && (
        <p className="field-hint ocena-stopka">
          {raport.wersjaMetodyki ? `Metodyka w wersji ${raport.wersjaMetodyki}.` : ""}
          {raport.modele.length > 0 ? ` Model: ${raport.modele.join(", ")}.` : ""}
        </p>
      )}
    </>
  );
}

/** Powod wygaszenia "Wyslij do recenzji" przy rozmowie konta uzytkownika (S-10: dymek mowi dlaczego). */
const POWOD_BEZ_RECENZJI_KONTA = "Rozmowa z konta użytkownika nie trafia do recenzji przed zgodą uczestników.";

export function OcenaMetodyki({
  sesjaId,
  status,
  maWypowiedzi,
  raport,
  onOcen,
  onWyslijDoRecenzji,
  rozmowaKontaUzytkownika = false,
  uproszczony = false,
  onPokazWypowiedz,
  powtarzaneSlowa,
}: {
  sesjaId: string;
  /** Status sesji: od niego zalezy, czy wolno zlecic ocene i pobrac raport. */
  status: string;
  maWypowiedzi: boolean;
  raport: RaportOceny;
  /**
   * Otwiera okno wyboru parametrow ("Ocen ponownie" albo pierwsza ocena). Brak = konto bez prawa do platnej oceny
   * (user): karta nie pokazuje wtedy ani "Ocen ponownie", ani "Ocen rozmowe".
   */
  onOcen?: () => void;
  /** Otwiera okno linku do recenzji. Brak = konto bez tej akcji; przycisk pojawia sie tez dopiero przy gotowej ocenie. */
  onWyslijDoRecenzji?: () => void;
  /**
   * Rozmowa konta uzytkownika: "Wyslij do recenzji" stoi wygaszony z powodem w dymku, tak jak "Ocen ponownie" i raport,
   * gdy serwer i tak by odmowil (link nie przyjmuje rozmow kont uzytkownikow, docs/recenzje.md).
   */
  rozmowaKontaUzytkownika?: boolean;
  /** Widok konta `user`: bez parametrow ze stanami technicznymi, bez nazwy modelu i bez wersji metodyki. */
  uproszczony?: boolean;
  /** Przewija transkrypt do wypowiedzi o tym indeksie (od 0); z listy "Powtarzane slowa" zaznacza tez trafienia slowa. */
  onPokazWypowiedz: PokazWypowiedz;
  /**
   * Powtarzane slowa po kluczu mowcy (Speaker.id), z lib/powtarzaneSlowa.ts. Brak = bez sekcji "Powtarzane slowa"; w widoku
   * uproszczonym sekcji nie ma nigdy.
   */
  powtarzaneSlowa?: PowtarzaneSlowaWgMowcy;
}): React.JSX.Element {
  const mozliwoscOceny = czyMoznaOcenic(status, maWypowiedzi);
  const liczbaPrzebiegow = raport.parametry.filter((p) => p.przebieg !== null).length;
  const mozliwoscRaportu = czyMoznaPobracRaport(status, liczbaPrzebiegow);

  const przyciskOceny = onOcen ? (
    <Button wariant="secondary" wylaczony={!mozliwoscOceny.mozna} onClick={onOcen}>
      <IconOdswiez />
      Oceń ponownie
    </Button>
  ) : null;

  const przyciskRecenzji =
    onWyslijDoRecenzji && mozliwoscRaportu.mozna ? (
      <Button wariant="primary" wylaczony={rozmowaKontaUzytkownika} onClick={onWyslijDoRecenzji}>
        <IconLink />
        Wyślij do recenzji
      </Button>
    ) : null;

  // B-2: w naglowku akcje wtorne (secondary) i co najwyzej jeden primary. "Wyslij do recenzji" jest nim tylko wtedy, gdy
  // jest gotowa ocena, ktora mozna wyslac; w stanie "nie oceniono" jedynym primary jest "Ocen rozmowe" w tresci karty.
  const akcje =
    raport.stan === "raport" ? (
      <>
        <PrzyciskRaportu sesjaId={sesjaId} mozliwosc={mozliwoscRaportu} />
        {przyciskOceny &&
          (mozliwoscOceny.mozna ? przyciskOceny : <Tooltip tekst={mozliwoscOceny.powod}>{przyciskOceny}</Tooltip>)}
        {przyciskRecenzji &&
          (rozmowaKontaUzytkownika ? (
            <Tooltip tekst={POWOD_BEZ_RECENZJI_KONTA}>{przyciskRecenzji}</Tooltip>
          ) : (
            przyciskRecenzji
          ))}
      </>
    ) : undefined;

  return (
    <Card tytul="Ocena wg metodyki" akcje={akcje}>
      {raport.stan === "czeka-na-podzial" && (
        <StanPusty
          tytul="Ocena ruszy po zatwierdzeniu podziału"
          opis="Zatwierdź podział tekstu na wypowiedzi w karcie powyżej. Wybrane parametry zostaną ocenione dopiero wtedy."
        />
      )}

      {raport.stan === "czeka-na-etapy" && <StanLadowania tekst="Ocena ruszy, gdy transkrypt będzie gotowy." />}

      {raport.stan === "brak-wypowiedzi" && (
        <StanPusty
          tytul="Nie ma czego oceniać"
          opis="Ta analiza nie ma transkryptu, więc nie ma wypowiedzi, które można ocenić."
        />
      )}

      {raport.stan === "nie-oceniono" && (
        <StanPusty
          tytul="Ta rozmowa nie została jeszcze oceniona"
          opis={
            !onOcen
              ? "Ocena pojawi się tutaj, gdy będzie gotowa."
              : mozliwoscOceny.mozna
                ? "Wybierz parametry metodyki, a model oceni rozmowę: pokaże mocne strony i rzeczy do poprawy razem z lepszymi wariantami."
                : mozliwoscOceny.powod
          }
          akcja={
            onOcen && mozliwoscOceny.mozna ? (
              <Button wariant="primary" onClick={onOcen}>
                Oceń rozmowę
              </Button>
            ) : undefined
          }
        />
      )}

      {raport.stan === "raport" && (
        <TrescRaportu
          raport={raport}
          onPokazWypowiedz={onPokazWypowiedz}
          uproszczony={uproszczony}
          powtarzaneSlowa={powtarzaneSlowa}
        />
      )}
    </Card>
  );
}
